// Kindra Health · Concept A · Product Platform · PREVIEW ONLY
//
// Direction: serious health-tech platform. The hero is a realistic
// multi-pane product interface mockup — left rail (health context),
// centre (chat answer), right (brief + memory). Below the hero, five
// product modules in a grid: Context · Chat · Reports · Brief · Memory.
// The visual language says "this is a system" instead of "this is a Q&A".
//
// Visual personality: dark navy hero canvas, realistic glass UI, tighter
// grid, more product, less poetry. Palette stays brand-consistent (paper
// + navy + teal) but the hero chrome is inverted for gravitas.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../services/activation';
import {
  PAPER, NAVY, NAVY_MUTED, NAVY_SOFT, TEAL, TEAL_SOFT, HAIRLINE, SAGE_BG,
  ArrowRight, CheckTick,
  ConceptNav, ConceptFooter,
} from './_shared';

// ─── Hero · realistic app mockup ──────────────────────────
const HeroA = ({ onStart }) => (
  <section
    className="relative overflow-hidden"
    style={{ backgroundColor: NAVY }}
    data-testid="a-hero"
  >
    {/* Subtle radial lift on the navy canvas */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(ellipse at 75% 30%, rgba(47,179,160,0.12), transparent 55%), radial-gradient(ellipse at 15% 90%, rgba(251,250,246,0.04), transparent 60%)',
      }}
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-24 lg:pb-28">
      <div className="max-w-[760px] mb-10 md:mb-14">
        <p
          className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5"
          style={{ color: TEAL, letterSpacing: '0.26em' }}
          data-testid="a-hero-eyebrow"
        >
          A health companion platform
        </p>
        <h1
          className="font-serif leading-[1.02] tracking-[-0.02em] font-normal text-[44px] sm:text-[60px] lg:text-[72px] xl:text-[80px]"
          style={{ color: PAPER }}
          data-testid="a-hero-headline"
        >
          Your health context,<br />
          <em className="italic" style={{ color: TEAL }}>finally working together.</em>
        </h1>
        <p
          className="font-sans mt-7 md:mt-8 text-[16px] md:text-[18.5px] leading-[1.55] max-w-[620px]"
          style={{ color: 'rgba(251,250,246,0.78)' }}
          data-testid="a-hero-subhead"
        >
          Kindra is one connected system for your reports, medications, symptoms, appointments and answers — built so nothing gets lost between visits.
        </p>
        <div className="mt-9 md:mt-10 flex flex-wrap items-center gap-5">
          <button
            onClick={onStart}
            className="group inline-flex items-center gap-2 rounded-full px-8 md:px-9 py-4 md:py-[20px] text-[15px] md:text-[16px] font-medium font-sans transition-all hover:translate-y-[-1px]"
            style={{ backgroundColor: PAPER, color: NAVY, boxShadow: '0 20px 40px -18px rgba(0,0,0,0.45)' }}
            data-testid="a-hero-cta"
          >
            Start free · 30 days
            <ArrowRight size={16} stroke={2} />
          </button>
          <span className="font-sans text-[12.5px] md:text-[13px]" style={{ color: 'rgba(251,250,246,0.55)' }}>
            No card needed · Private by design
          </span>
        </div>
      </div>

      {/* ── Product mockup · 3-pane desktop app shell ── */}
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(251,250,246,0.98), #FBFAF6)',
          border: '1px solid rgba(251,250,246,0.12)',
          boxShadow: '0 50px 100px -35px rgba(0,0,0,0.6)',
        }}
        data-testid="a-hero-mockup"
      >
        {/* window chrome */}
        <div className="flex items-center gap-2 px-5 py-3.5" style={{ backgroundColor: '#EDEAE0', borderBottom: `1px solid ${HAIRLINE}` }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#F0A88E' }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#F2D27A' }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#9FC9B2' }} />
          <span className="ml-4 font-sans text-[11.5px]" style={{ color: NAVY_SOFT, letterSpacing: '0.05em' }}>
            kindrahealth.app · your dashboard
          </span>
        </div>

        <div className="grid grid-cols-12">
          {/* LEFT rail · context */}
          <aside className="hidden md:block col-span-3 px-5 py-6" style={{ backgroundColor: SAGE_BG, borderRight: `1px solid ${HAIRLINE}` }}>
            <p className="font-sans text-[10px] uppercase font-medium mb-4" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Your profile</p>
            {[
              { label: 'Conditions', value: 'Iron deficiency · Hypothyroid' },
              { label: 'Medications', value: 'Sertraline 50mg · Ferro-liquid' },
              { label: 'Upcoming', value: 'GP · Friday 10:30am' },
              { label: 'Latest report', value: 'Iron studies · 14 Mar' },
            ].map((row, i) => (
              <div key={i} className="mb-5">
                <p className="font-sans text-[10.5px] uppercase mb-1" style={{ color: NAVY_SOFT, letterSpacing: '0.16em' }}>{row.label}</p>
                <p className="font-serif text-[14px] leading-snug" style={{ color: NAVY }}>{row.value}</p>
              </div>
            ))}
            <div className="mt-8 pt-5" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
              <p className="font-sans text-[10.5px] uppercase mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.16em' }}>In your record</p>
              <ul className="space-y-2 font-sans text-[12.5px]" style={{ color: NAVY_MUTED }}>
                <li className="flex justify-between"><span>Reports</span><span style={{ color: NAVY }}>4</span></li>
                <li className="flex justify-between"><span>Notes</span><span style={{ color: NAVY }}>9</span></li>
                <li className="flex justify-between"><span>Appointments</span><span style={{ color: NAVY }}>2</span></li>
              </ul>
            </div>
          </aside>

          {/* CENTRE · chat */}
          <div className="col-span-12 md:col-span-6 px-5 md:px-6 py-6 md:py-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="font-sans text-[10.5px] uppercase font-medium" style={{ color: TEAL, letterSpacing: '0.2em' }}>Live conversation</span>
              <span className="flex-1 h-px" style={{ backgroundColor: HAIRLINE }} />
              <span className="font-sans text-[10.5px]" style={{ color: NAVY_SOFT }}>3 min ago</span>
            </div>

            <div className="flex justify-end mb-4">
              <div className="max-w-[82%] rounded-2xl rounded-tr-sm px-4 py-3" style={{ backgroundColor: NAVY, color: PAPER }}>
                <p className="font-sans text-[14px] leading-[1.45]">Why am I still exhausted?</p>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap gap-1.5">
              {['Ferritin · low', 'Fatigue · 3 weeks', 'GP · Friday'].map((p, i) => (
                <span key={i} className="inline-flex items-center rounded-full px-2.5 py-1 font-sans text-[11px]" style={{ backgroundColor: TEAL_SOFT, color: TEAL }}>
                  {p}
                </span>
              ))}
            </div>

            <div className="flex justify-start mb-4">
              <div className="max-w-[92%] rounded-2xl rounded-tl-sm px-5 py-4" style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}>
                <p className="font-sans text-[14px] md:text-[14.5px] leading-[1.55]" style={{ color: NAVY }}>
                  Your ferritin was low in March, fatigue has worsened, and your GP visit is Friday. I've added three questions and your recent results to your appointment brief.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ backgroundColor: '#F0F9F6', border: `1px solid ${TEAL_SOFT}` }}>
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}>📋</span>
              <p className="flex-1 font-sans text-[13px]" style={{ color: NAVY }}>3 questions + recent results added to Friday brief</p>
              <CheckTick size={14} />
            </div>
          </div>

          {/* RIGHT · brief + memory */}
          <aside className="hidden lg:block col-span-3 px-5 py-6" style={{ backgroundColor: PAPER, borderLeft: `1px solid ${HAIRLINE}` }}>
            <p className="font-sans text-[10px] uppercase font-medium mb-4" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Appointment brief</p>
            <div className="rounded-lg p-3.5 mb-5" style={{ backgroundColor: SAGE_BG, border: `1px solid ${HAIRLINE}` }}>
              <p className="font-serif text-[14px] mb-1" style={{ color: NAVY }}>GP · Friday 10:30am</p>
              <ul className="font-sans text-[12.5px] space-y-1" style={{ color: NAVY_MUTED }}>
                <li>· Fatigue · 3 weeks worse</li>
                <li>· Ferritin 11 on March study</li>
                <li>· 3 questions prepared</li>
              </ul>
              <span className="inline-flex items-center mt-2 text-[10px] font-medium uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: TEAL_SOFT, color: TEAL, letterSpacing: '0.14em' }}>Ready</span>
            </div>

            <p className="font-sans text-[10px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Recent activity</p>
            <ul className="space-y-3 font-sans text-[12px]" style={{ color: NAVY_MUTED }}>
              <li className="flex items-start gap-2"><span style={{ color: TEAL }}>●</span><span><b style={{ color: NAVY }}>14 Mar</b> · Iron studies uploaded</span></li>
              <li className="flex items-start gap-2"><span style={{ color: TEAL }}>●</span><span><b style={{ color: NAVY }}>12 Mar</b> · Chat about iron</span></li>
              <li className="flex items-start gap-2"><span style={{ color: TEAL }}>●</span><span><b style={{ color: NAVY }}>8 Mar</b> · Started Sertraline</span></li>
            </ul>
          </aside>
        </div>
      </div>
    </div>
  </section>
);

// ─── Modules · first scroll ───────────────────────────────
const ModulesA = () => {
  const modules = [
    { code: '01', name: 'Context',  line: 'Every condition, medication and history point in one profile.' },
    { code: '02', name: 'Chat',     line: 'Answers that reference your reports — never generic.' },
    { code: '03', name: 'Reports',  line: 'Pathology, imaging and GP letters translated into plain English.' },
    { code: '04', name: 'Brief',    line: 'A short, editable one-page brief before every appointment.' },
    { code: '05', name: 'Memory',   line: 'Everything quietly carries forward — so nothing gets lost.' },
  ];
  return (
    <section className="relative" style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }} data-testid="a-modules">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-[640px]">
            <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>
              Five modules · one companion
            </p>
            <h2 className="font-serif text-[32px] md:text-[44px] lg:text-[52px] leading-[1.07] tracking-[-0.015em] font-normal" style={{ color: NAVY }}>
              Not a chatbot. A <em className="italic">connected system.</em>
            </h2>
          </div>
          <p className="font-sans text-[14px] md:text-[15px] leading-[1.6] max-w-[360px]" style={{ color: NAVY_MUTED }}>
            Each module carries context into the next. Upload a report, and it shows up in chat, in the next brief, and in your record — automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px rounded-2xl overflow-hidden" style={{ backgroundColor: HAIRLINE, border: `1px solid ${HAIRLINE}` }}>
          {modules.map((m, i) => (
            <article
              key={m.code}
              className="flex flex-col justify-between p-6 md:p-7 min-h-[220px] transition-colors hover:bg-[#F4F0E6]"
              style={{ backgroundColor: PAPER }}
              data-testid={`a-module-${i}`}
            >
              <div>
                <p className="font-sans text-[10px] uppercase font-medium mb-3" style={{ color: TEAL, letterSpacing: '0.26em' }}>{m.code}</p>
                <p className="font-serif text-[22px] leading-tight mb-3" style={{ color: NAVY }}>{m.name}</p>
              </div>
              <p className="font-sans text-[13.5px] leading-[1.55]" style={{ color: NAVY_MUTED }}>{m.line}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Product proof · three-panel walk-through ────────────
const ProofA = () => (
  <section className="relative" style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }} data-testid="a-proof">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
      <div className="max-w-[720px] mb-12 md:mb-16">
        <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>How it works · in three moves</p>
        <h2 className="font-serif text-[32px] md:text-[44px] lg:text-[52px] leading-[1.07] tracking-[-0.015em] font-normal" style={{ color: NAVY }}>
          Upload. <em className="italic">Understand.</em> Walk in prepared.
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {[
          {
            step: '01',
            title: 'Upload a report',
            body: 'Drag in any pathology, imaging or GP letter. Kindra extracts what matters in seconds.',
            visual: (
              <div className="rounded-lg p-4" style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}>
                <p className="font-sans text-[10.5px] uppercase mb-2" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Pathology · 14 Mar</p>
                <div className="space-y-1.5 font-sans text-[12.5px]" style={{ color: NAVY_MUTED }}>
                  <div className="flex justify-between"><span>Ferritin</span><span><b style={{ color: NAVY }}>11 µg/L</b> <span style={{ color: TEAL }}>low</span></span></div>
                  <div className="flex justify-between"><span>Haemoglobin</span><span>132 g/L</span></div>
                  <div className="flex justify-between"><span>TSH</span><span>2.4 mIU/L</span></div>
                </div>
              </div>
            ),
          },
          {
            step: '02',
            title: 'Ask Kindra anything',
            body: 'Questions answered in light of your full profile — not generic internet answers.',
            visual: (
              <div className="rounded-lg p-4" style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}>
                <p className="font-sans text-[13px] mb-2" style={{ color: NAVY }}>"Does my ferritin matter?"</p>
                <p className="font-sans text-[12.5px] leading-[1.55]" style={{ color: NAVY_MUTED }}>
                  Your March ferritin was 11 — low. Given your fatigue, it's worth raising Friday. I've added 3 questions for your GP.
                </p>
              </div>
            ),
          },
          {
            step: '03',
            title: 'Walk in prepared',
            body: 'Every visit starts with a one-page brief — concerns, context, medications, questions.',
            visual: (
              <div className="rounded-lg p-4" style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}>
                <p className="font-sans text-[10.5px] uppercase mb-2" style={{ color: TEAL, letterSpacing: '0.18em' }}>Appointment brief</p>
                <p className="font-serif text-[15px] mb-2" style={{ color: NAVY }}>GP · Friday 10:30am</p>
                <ul className="space-y-1 font-sans text-[12.5px]" style={{ color: NAVY_MUTED }}>
                  <li>· 3 main concerns</li>
                  <li>· 4 questions ready</li>
                  <li>· Medication list current</li>
                </ul>
              </div>
            ),
          },
        ].map((s, i) => (
          <article key={i} className="rounded-2xl p-7 md:p-8 flex flex-col gap-5" style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }} data-testid={`a-proof-step-${i}`}>
            <div>
              <p className="font-sans text-[10.5px] uppercase font-medium mb-3" style={{ color: TEAL, letterSpacing: '0.22em' }}>Step {s.step}</p>
              <p className="font-serif text-[22px] leading-tight mb-2" style={{ color: NAVY }}>{s.title}</p>
              <p className="font-sans text-[14px] leading-[1.55]" style={{ color: NAVY_MUTED }}>{s.body}</p>
            </div>
            {s.visual}
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ─── Trust ───────────────────────────────────────────────
const TrustA = () => (
  <section className="relative" style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }} data-testid="a-trust">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>Built on trust</p>
          <h2 className="font-serif text-[32px] md:text-[40px] leading-[1.1] tracking-[-0.012em] font-normal" style={{ color: NAVY }}>
            Health information deserves <em className="italic">extra care.</em>
          </h2>
        </div>
        <ul className="space-y-4">
          {[
            'Private by design — your data is never sold.',
            'Never prescribes, never diagnoses.',
            'Supports your clinician — never replaces them.',
            'Built in Brisbane, Australia.',
          ].map((l, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`a-trust-${i}`}>
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full mt-0.5" style={{ backgroundColor: TEAL_SOFT }}>
                <CheckTick size={13} />
              </span>
              <p className="font-sans text-[15px] md:text-[16px] leading-[1.55]" style={{ color: NAVY }}>{l}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// ─── Final CTA ───────────────────────────────────────────
const CTAA = ({ onStart }) => (
  <section className="relative" style={{ backgroundColor: NAVY, color: PAPER }} data-testid="a-cta">
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 text-center">
      <h2 className="font-serif text-[40px] md:text-[56px] lg:text-[64px] leading-[1.04] tracking-[-0.018em] font-normal max-w-[900px] mx-auto">
        A real system for <em className="italic" style={{ color: TEAL }}>your whole health story.</em>
      </h2>
      <p className="font-sans mt-6 md:mt-8 text-[16px] md:text-[18px] leading-[1.55] max-w-[600px] mx-auto" style={{ color: 'rgba(251,250,246,0.72)' }}>
        Start free for 30 days. If it earns a place in your week, A$9.99/month after — cancel in one click.
      </p>
      <button
        onClick={onStart}
        className="mt-10 md:mt-12 group inline-flex items-center gap-2 rounded-full px-8 md:px-10 py-4 md:py-[20px] text-[14px] md:text-[16px] font-medium font-sans transition-colors"
        style={{ backgroundColor: PAPER, color: NAVY }}
        data-testid="a-cta-button"
      >
        Start free · no card needed
        <ArrowRight size={16} stroke={2} />
      </button>
    </div>
  </section>
);

// ─── Page ────────────────────────────────────────────────
const ConceptAPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    try { track('concept_viewed', { concept: 'A' }); } catch { /* noop */ }
  }, []);
  const goStart = () => { try { track('clicked_start_free', { src: 'concept_a' }); } catch {} navigate('/register'); };
  const goLogin = () => navigate('/login');
  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: PAPER, color: NAVY }} data-testid="concept-a-page">
      <ConceptNav onStart={goStart} onLogin={goLogin} user={user} activeConcept="A" />
      <HeroA onStart={goStart} />
      <ModulesA />
      <ProofA />
      <TrustA />
      <CTAA onStart={goStart} />
      <ConceptFooter />
    </div>
  );
};

export default ConceptAPage;
